import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import MKButton from "./MKButton";
import MKTypography from "./MKTypography";
import API_BASE_URL from "../apiConfig";
import { useTheme } from "../ThemeContext";
import { 
  PRIMARY_PINK, 
  PRIMARY_PINK_DARK, 
  TRANSPARENT_BLACK_08,
  TRANSPARENT_PINK_05,
  TRANSPARENT_PINK_1,
  TRANSPARENT_PINK_2,
  TRANSPARENT_PINK_3,
  TRANSPARENT_PINK_5,
  BORDER_LIGHT
} from "../constants";

function EmailRSVPModal({ open, onClose, clubId, clubName, event }) {
  const { darkMode } = useTheme();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [sendingProgress, setSendingProgress] = useState(0);
  const [totalRecipients, setTotalRecipients] = useState(0);

  // Set default subject and body when event changes
  useEffect(() => {
    if (event) {
      setSubject(`Event Update: ${event.title}`);
      setBody(`Hello RSVP'd attendees for "${event.title}",\n\n`);
    }
  }, [event]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    
    // Validation
    if (!subject.trim()) {
      setError("Subject is required");
      return;
    }
    
    if (!body.trim()) {
      setError("Email body is required");
      return;
    }

    if (subject.trim().length > 100) {
      setError("Subject must be less than 100 characters");
      return;
    }

    if (body.trim().length > 5000) {
      setError("Email body must be less than 5000 characters");
      return;
    }

    // Get recipient count first
    try {
      const countResponse = await fetch(`${API_BASE_URL}/api/events/${event.id}/rsvp-recipient-count`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({}),
      });

      if (!countResponse.ok) {
        const errorData = await countResponse.json();
        throw new Error(errorData.error || "Failed to get recipient count");
      }

      const countResult = await countResponse.json();
      setRecipientCount(countResult.recipient_count);
      setTotalRecipients(countResult.recipient_count);
      
      if (countResult.recipient_count === 0) {
        setError("No RSVP'd attendees found for this event");
        return;
      }

      // Show confirmation dialog
      setShowConfirmation(true);
      
    } catch (error) {
      console.error("Get recipient count error:", error);
      setError(error.message || "Failed to get recipient count");
    }
  };

  const handleConfirmSend = async () => {
    setShowConfirmation(false);
    setLoading(true);
    setSendingProgress(0);
    
    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setSendingProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 10;
      });
    }, 200);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${event.id}/send-rsvp-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          subject: subject.trim(),
                  body: body.trim()
      }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send email");
      }

      const result = await response.json();
      clearInterval(progressInterval);
      setSendingProgress(100);
      setSuccess(`Email sent successfully to ${result.recipient_count} RSVP'd attendees!`);
      
      // Clear form
      setSubject(`Event Update: ${event.title}`);
      setBody(`Hello RSVP'd attendees for "${event.title}",\n\n`);
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
        setSuccess("");
        setSendingProgress(0);
      }, 2000);
      
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Send email error:", error);
      setError(error.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSend = () => {
    setShowConfirmation(false);
  };

  const handleClose = () => {
    setError("");
    setSuccess("");
    setSubject(`Event Update: ${event.title}`);
    setBody(`Hello RSVP'd attendees for "${event.title}",\n\n`);
    setShowConfirmation(false);
    setRecipientCount(0);
    setSendingProgress(0);
    setTotalRecipients(0);
    onClose();
  };

  if (!event) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
                 <MKTypography variant="h5" component="div" sx={{ color: 'text.primary' }}>
           Email RSVP'd Attendees
         </MKTypography>
         <MKTypography variant="body2" color="text.secondary">
           {clubName} - {event.title}
         </MKTypography>
         <MKTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
           This email will be sent to all attendees who have RSVP'd for this event.
         </MKTypography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {/* Subject Field */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="subject"
            label="Subject"
            name="subject"
            autoFocus
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject"
            helperText={`${subject.length}/100 characters`}
            error={subject.length > 100}
            sx={{
              mb: 2,
              fontSize: 18,
              height: 56,
              '& .MuiInputBase-input': {
                color: 'text.primary',
                py: 1.5,
                fontSize: 18,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: TRANSPARENT_PINK_3,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: TRANSPARENT_PINK_5,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY_PINK,
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: PRIMARY_PINK,
              },
              '& .MuiFormHelperText-root': {
                color: 'text.secondary',
              },
              '& .MuiFormHelperText-root.Mui-error': {
                color: 'error.main',
              },
            }}
          />

          {/* Email Body Field */}
          <TextField
            margin="normal"
            required
            fullWidth
            name="body"
            label="Email Body"
            id="body"
            multiline
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Enter your message to RSVP'd attendees..."
            helperText={`${body.length}/5000 characters`}
            error={body.length > 5000}
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                color: 'text.primary',
                fontSize: 16,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: TRANSPARENT_PINK_3,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: TRANSPARENT_PINK_5,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY_PINK,
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: PRIMARY_PINK,
              },
              '& .MuiFormHelperText-root': {
                color: 'text.secondary',
              },
              '& .MuiFormHelperText-root.Mui-error': {
                color: 'error.main',
              },
            }}
          />

          {/* Preview Section */}
          {subject && body && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: TRANSPARENT_PINK_05, 
              borderRadius: 1, 
              border: `1px solid ${TRANSPARENT_PINK_2}` 
            }}>
              <MKTypography variant="h6" fontWeight="bold" sx={{ color: PRIMARY_PINK, mb: 1 }}>
                📧 Email Preview
              </MKTypography>
              <MKTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'text.primary' }}>
                Subject: {subject}
              </MKTypography>
              <MKTypography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {body}
              </MKTypography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
                 <Button 
           onClick={handleClose} 
           variant="outlined"
           disabled={loading}
           sx={{
             color: darkMode ? '#ffffff' : '#000000',
             borderColor: darkMode ? '#ffffff' : '#000000',
             fontWeight: 600,
             textTransform: "none",
             "&:hover": {
               borderColor: darkMode ? '#ffffff' : '#000000',
               backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
             }
           }}
         >
           Cancel
         </Button>
        <MKButton
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !subject.trim() || !body.trim()}
          sx={{
            bgcolor: PRIMARY_PINK,
            color: "white !important",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              bgcolor: PRIMARY_PINK_DARK,
              color: "white !important"
            },
            "&.Mui-disabled": {
              bgcolor: "#ccc !important",
              color: "#666666 !important"
            },
            "& .MuiButton-label": {
              color: "white !important"
            }
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Sending...
            </>
          ) : (
            "Send Email"
          )}
        </MKButton>
      </DialogActions>

      {/* Progress Bar */}
      {loading && sendingProgress > 0 && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <MKTypography variant="body2" color="text.secondary">
              Sending emails...
            </MKTypography>
            <MKTypography variant="body2" color="text.secondary">
              {Math.round(sendingProgress)}%
            </MKTypography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={sendingProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: TRANSPARENT_PINK_1,
              '& .MuiLinearProgress-bar': {
                backgroundColor: PRIMARY_PINK,
                borderRadius: 4,
              }
            }}
          />
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog 
        open={showConfirmation} 
        onClose={handleCancelSend}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <MKTypography variant="h6" component="div" sx={{ color: 'text.primary' }}>
            Confirm Email Send
          </MKTypography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ textAlign: "center", py: 2 }}>
            <MKTypography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
              Are you sure you want to send this email to{' '}
              <strong style={{ color: PRIMARY_PINK }}>{recipientCount}</strong> recipients?
            </MKTypography>
            
            <Box sx={{ 
              p: 2, 
              bgcolor: TRANSPARENT_PINK_05, 
              borderRadius: 1, 
              border: `1px solid ${TRANSPARENT_PINK_2}`,
              textAlign: 'left'
            }}>
              <MKTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'text.primary' }}>
                Subject: {subject}
              </MKTypography>
              <MKTypography variant="body2" sx={{ color: 'text.secondary' }}>
                {body.length > 100 ? `${body.substring(0, 100)}...` : body}
              </MKTypography>
            </Box>
            
            <MKTypography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Recipients: RSVP'd Attendees Only
            </MKTypography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancelSend} 
            variant="outlined"
            sx={{
              color: darkMode ? '#ffffff' : '#000000',
              borderColor: darkMode ? '#ffffff' : '#000000',
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                borderColor: darkMode ? '#ffffff' : '#000000',
                backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <MKButton
            variant="contained"
            onClick={handleConfirmSend}
            sx={{
              bgcolor: PRIMARY_PINK,
              color: "white !important",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                bgcolor: PRIMARY_PINK_DARK,
                color: "white !important"
              },
              "& .MuiButton-label": {
                color: "white !important"
              }
            }}
          >
            Send to {recipientCount} Recipients
          </MKButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

EmailRSVPModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clubId: PropTypes.string.isRequired,
  clubName: PropTypes.string.isRequired,
  event: PropTypes.object.isRequired,
};

export default EmailRSVPModal;
